import { useCallback, useRef } from 'react';

/**
 * A bar that stands over the page, measured into the custom property the page
 * pads by.
 *
 * Three objects are pinned to the bottom of a screen — the regent's `.panel`,
 * the `.dock` under a form, the `.undo` bar — and each hides content unless the
 * page keeps exactly its height clear underneath. Two of the three measured
 * themselves by hand with the same fifteen lines; the third declared a constant
 * in the stylesheet instead, and the constant is what this exists to end.
 * `--panel` said 67px — one 46px button and its padding — while the panel with
 * its menu open is 125, so 58px of the screen stood behind it and the sentence
 * above it was cut through the middle. Reported 2026-09-07.
 *
 * **It hands back a ref callback rather than taking a ref**, and that is not a
 * matter of taste: an effect keyed on a ref runs once, and on a screen that
 * draws nothing until its first answer arrives it runs while the bar does not
 * exist yet — measuring nothing and never being asked again. A ref callback is
 * called with the element when it appears and with null when it goes, which is
 * exactly the life of a bar that comes and goes with what is on screen. This
 * was the first version's own bug, found by measuring the panel and seeing the
 * property still unset.
 *
 * Measured by hand before the observer is asked: a `ResizeObserver` reports on
 * the frame after the one it was made in, and until it does the content below
 * is padded for a bar of nothing.
 *
 * On the way out the property is removed rather than zeroed, so a value
 * declared in `:root` comes back instead of being overwritten with 0.
 */
export function useMeasuredBar(name: string): (el: HTMLElement | null) => void {
  const watching = useRef<ResizeObserver | null>(null);

  return useCallback(
    (el: HTMLElement | null) => {
      const root = document.documentElement;
      watching.current?.disconnect();
      watching.current = null;
      if (el === null) {
        // Gone, and the padding goes with it, or the screen keeps room for a
        // bar that has left.
        root.style.removeProperty(name);
        return;
      }
      const measure = (): void => {
        root.style.setProperty(name, `${Math.ceil(el.getBoundingClientRect().height)}px`);
      };
      measure();
      watching.current = new ResizeObserver(measure);
      watching.current.observe(el);
    },
    [name],
  );
}
